import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { activitySidebarService } from "../../services/activitySidebar.service";

const POLL_INTERVAL_MS = 30_000;

// SIMS-4162: the "Today's Activities" call list for the sidebar.
export const useActivityCalls = (
	coordinatorId: string | undefined,
	includeDismissed: boolean,
) => {
	return useQuery({
		queryKey: ["sims-activity-calls", coordinatorId, includeDismissed],
		queryFn: () =>
			activitySidebarService.getCalls(
				coordinatorId as string,
				includeDismissed,
			),
		enabled: Boolean(coordinatorId),
		staleTime: 15_000,
		gcTime: 300_000,
		refetchInterval: POLL_INTERVAL_MS,
		refetchIntervalInBackground: false,
		refetchOnWindowFocus: true,
	});
};

// Invalidate both the calls list and the sidebar counts so the section badge
// stays in sync after a dismiss / un-dismiss.
const useCallMutation = (
	coordinatorId: string | undefined,
	action: (coordinatorId: string, callId: string) => Promise<void>,
) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (callId: string) => {
			if (!coordinatorId) {
				throw new Error("coordinatorId is required to mutate a call");
			}
			return action(coordinatorId, callId);
		},
		onSuccess: () => {
			// Prefix-match on purpose: this 2-element key invalidates every
			// ["sims-activity-calls", coordinatorId, includeDismissed] variant
			// so both the active and show-dismissed views refetch after a change.
			queryClient.invalidateQueries({
				queryKey: ["sims-activity-calls", coordinatorId],
			});
			queryClient.invalidateQueries({
				queryKey: ["sims-activity-sidebar", coordinatorId],
			});
		},
	});
};

export const useDismissCall = (coordinatorId: string | undefined) =>
	useCallMutation(coordinatorId, activitySidebarService.dismissCall);

export const useUndismissCall = (coordinatorId: string | undefined) =>
	useCallMutation(coordinatorId, activitySidebarService.undismissCall);
